import React from 'react';
import { useAgentConversations } from '../../../contexts/AgentConversationsContext';
import { useSidePanel } from '../../../contexts/SidePanelContext';
import { useIsMobile } from '../../../hooks/useIsMobile';
import PatientContextChip from '../../agent-context/PatientContextChip';
import ColumnResizeHandle from './ColumnResizeHandle';
import ConversationList from './ConversationList';
import ConversationStart from './ConversationStart';
import ConversationThread from './ConversationThread';
import { PanelTopBar } from './PanelChrome';
import { MAIN_MIN_WIDTH, PANEL_LIMITS, usePanelWidth } from './usePanelWidth';

const MAIN_ID = 'contenido';

// Sissi AI on a desktop: one panel docked on the right of the page, resizable
// from its left edge. It shows the conversation list or one conversation;
// the page keeps the centre. On a phone Sissi AI stays a bottom sheet
// (ClinicAgentPanel).
const AgentPanel: React.FC = () => {
  const agent = useAgentConversations();
  const sidePanel = useSidePanel();
  const isMobile = useIsMobile();
  const { width, preview, commit } = usePanelWidth();

  if (!agent || isMobile || !sidePanel?.agentOpen) return null;

  // The most the panel may grow right now: the page keeps MAIN_MIN_WIDTH.
  const getMax = () => {
    const main = document.getElementById(MAIN_ID)?.getBoundingClientRect().width ?? Infinity;
    const room = Math.floor(width + main - MAIN_MIN_WIDTH);
    return Math.max(PANEL_LIMITS.min, Math.min(PANEL_LIMITS.max, room));
  };

  const close = () => sidePanel.closeAgent();

  let body: React.ReactNode;
  if (agent.view === 'list') {
    body = <ConversationList onClose={close} panel />;
  } else if (agent.active) {
    body = (
      <ConversationThread
        onClose={close}
        panel
        renderPatientContext={(patient) => <PatientContextChip patient={patient} />}
      />
    );
  } else {
    body = (
      <>
        <PanelTopBar
          state={agent.connectionState}
          toReview={agent.unread.size}
          onShowList={agent.showList}
          onNew={() => agent.startDraft(agent.routePatient?.name ? agent.routePatient : null)}
          onClose={close}
          panel
        />
        <div className="flex min-h-0 flex-1 overflow-y-auto">
          <ConversationStart />
        </div>
      </>
    );
  }

  return (
    <>
      <ColumnResizeHandle
        label="Ancho del panel de Sissi AI"
        value={width}
        min={PANEL_LIMITS.min}
        getMax={getMax}
        onPreview={preview}
        onCommit={commit}
        growsTo="left"
      />
      <aside
        aria-label="Sissi AI"
        style={{ width }}
        className="sticky top-0 box-border flex h-screen shrink-0 flex-col self-start border-l border-border bg-card"
      >
        {body}
      </aside>
    </>
  );
};

export default AgentPanel;
